import React, { useCallback, useMemo } from "react";
import useUserDetail from "@hooks/useUserDetail";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import {
    Box,
    Button,
    Checkbox,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mzc-pdc/ui";
import StatusBadge from "@components_v2/status-badge/status-badge";
import TableCard from "@components_v2/card/table-card";
import { Link } from "react-router-dom";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import convertToByte from "@cores/convertToByte";
import { alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import UserDetailPopper from "@features/user-detail-popper/user-detail-popper";
import { ARCHIVED_ASSET_STATUS, ASSET_PREVIEW_STATUS, MEDIA_TYPES, MENU_LIST } from "@constants";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import moment from "moment";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import { IconAi, IconDrm, IconLink, IconLock, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import useSettings from "@hooks/useSettings";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { canNavigateToArchiveDetail } from "@utils/archive-utils";
import { EllipsisTypography, OverflowTooltip } from "@components_v2/ellipsis-tooltip";

const TableList = ({
    keyPrefix,
    data,
    columns,
    selected,
    clicked,
    thumbnailCrop,
    type,
    actionButton,
    actionButtonProps,
    showColumns = false,
    disableHover,
    onClickName,
    onClickCheck,
    onClickRow,
    showAdditionalIcons = false,
}) => {
    const { t } = useTranslation();
    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();
    const { navigatorMenus } = useSettings({ type: "General" });

    const tableMinWidth = useMemo(() => {
        let totalWidth = 0;

        columns.forEach((column) => {
            if (column.id !== "name" && column.style?.custom?.width) {
                const width = column.style.custom.width;
                const numericWidth =
                    typeof width === "string"
                        ? parseInt(width.replace("px", ""))
                        : typeof width === "number"
                        ? width
                        : 0;
                totalWidth += numericWidth;
            }
        });

        totalWidth += 200;

        if (onClickCheck) {
            totalWidth += 32;
        }

        if (actionButton) {
            if (actionButtonProps?.width) {
                const actionWidth =
                    typeof actionButtonProps.width === "string"
                        ? parseInt(actionButtonProps.width.replace("px", ""))
                        : typeof actionButtonProps.width === "number"
                        ? actionButtonProps.width
                        : 50;
                totalWidth += actionWidth;
            } else {
                totalWidth += 50;
            }
        }

        return totalWidth;
    }, [columns, onClickCheck, actionButton, actionButtonProps]);

    const getNameThumbnail = useCallback(
        (type, option) => {
            switch (type) {
                case "asset":
                case "share_asset": {
                    let src;

                    const commonStatusPreviews = option?.previews?.filter(
                        (p) => p.status !== ASSET_PREVIEW_STATUS.ERROR,
                    );
                    const previewStatus =
                        commonStatusPreviews?.length === 0 && option.previews?.length > 0
                            ? ASSET_PREVIEW_STATUS.ERROR
                            : commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;

                    if (commonStatusPreviews?.[0]?.accessUrl) src = commonStatusPreviews?.[0]?.accessUrl;
                    if (!src) {
                        if (option?.mediaType === MEDIA_TYPES.IMAGE) {
                            src = option.accessUrls?.[0] ?? option.elements?.[0]?.accessUrl;
                        } else if (option.mediaType === MEDIA_TYPES.VIDEO) src = option.thumbnails?.[0]?.accessUrl;
                    }

                    return (
                        <AssetThumbnail
                            variant={"table"}
                            src={src ?? ""}
                            minWidth={52}
                            width={52}
                            height={52}
                            thumbnailCrop={thumbnailCrop}
                            showIcons={type === "asset"}
                            asset={option}
                            disableHover={disableHover}
                            errorIconProps={{ position: "left", size: 16 }}
                            previewStatus={previewStatus}
                        />
                    );
                }
                case "collection":
                    return <CollectionThumbnail collection={option} size={"xsmall"} variant={"table"} />;
            }
        },
        [data],
    );

    const getNameIcon = useCallback(
        (type, option) => {
            switch (type) {
                case "asset":
                case "share_asset":
                    return <AssetTypeFlag size={"small"} type={option.mediaType} />;
            }
        },
        [data],
    );

    const getLinkComponent = useCallback(
        (type, item) => {
            if (
                type.toUpperCase() === "ASSET" &&
                canNavigateToArchiveDetail(item.archiveStatus) &&
                !navigatorMenus?.[MENU_LIST.ARCHIVED]?.visible
            ) {
                return (
                    <Tooltip
                        title={
                            // 이 분기는 링크가 아니라 사유 안내가 뜬다. 이름이 잘려도 읽을 방법이 없어
                            // 같은 툴팁 첫 줄에 이름을 함께 담는다. (툴팁이 두 개가 되지 않게)
                            <>
                                <Box component={"span"} sx={{ display: "block", fontWeight: 500 }}>
                                    {item.name}
                                </Box>
                                {t(
                                    `common::msg::It's not possible to proceed as the archived menu isn't being used.`,
                                    `It's not possible to proceed as the archived menu isn't being used.`,
                                )}
                            </>
                        }
                    >
                        <Box
                            // width:fit-content 이면 부모 폭을 넘어도 줄어들지 않아 말줄임이 걸리지 않는다.
                            // 잘림 표시는 이 Box 가 맡는다. (DAM-654 · PR #531 리뷰 반영)
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            // 툴팁 title 이 문자열이 아니면 MUI 가 aria-label 을 넣지 않는다(열렸을 때만 aria-labelledby).
                            // 이름과 사유를 두 줄로 보여주면서 접근 가능한 이름은 여기서 직접 준다.
                            aria-label={`${item.name} — ${t(
                                `common::msg::It's not possible to proceed as the archived menu isn't being used.`,
                                `It's not possible to proceed as the archived menu isn't being used.`,
                            )}`}
                        >
                            <Typography variant={"body1_500"} component={"span"} noWrap>
                                {item.name}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            }

            return (
                <OverflowTooltip title={item.name}>
                    <Link
                        style={{
                            color: theme.palette.greySecondary.main,
                            fontWeight: 500,
                            // 잘림 여부를 이 요소에서 측정하므로 말줄임 스타일을 직접 지정한다. (Link 는 noWrap prop 미지원)
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        to={onClickName(item)}
                    >
                        {item.name}
                    </Link>
                </OverflowTooltip>
            );
        },
        [navigatorMenus, onClickName],
    );

    const getTableValueByColumn = useCallback((columnId, columnType, option) => {
        switch (columnType) {
            case "status":
                return (
                    <Grid container justifyContent={"center"}>
                        <StatusBadge status={option?.[columnId]} />
                    </Grid>
                );
            case "name":
                return (
                    <TableCard
                        thumbnail={getNameThumbnail(type, option)}
                        name={{
                            name: onClickName ? (
                                <>{getLinkComponent(type, option)}</>
                            ) : (
                                <EllipsisTypography variant={"body1_500"} component={"span"} noWrap>
                                    {option?.name}
                                </EllipsisTypography>
                            ),
                            startIcon: getNameIcon(type, option),
                            endIcon: showAdditionalIcons && type === "asset" && (
                                <Stack direction={"row"} gap={theme.spacing(0.5)} sx={{ alignItems: "center" }}>
                                    {option?.isConnected ? <IconLink size={16} /> : ""}
                                    {option?.myFavorite && (
                                        <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                            <IconStar size={16} htmlColor={theme.palette.etc.orange500} />
                                        </Box>
                                    )}
                                    {option?.hasDubbing && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"etc.purple400"}
                                            color="secondary.contrastText"
                                        >
                                            <IconVoice size={12} />
                                        </Box>
                                    )}
                                    {option?.encryptions && option?.encryptions.indexOf("DRM") > -1 && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"warning.main"}
                                            color={theme.palette.constant.white}
                                        >
                                            <IconDrm size={12} />
                                        </Box>
                                    )}
                                    {option?.isAnalyzed && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"success.light"}
                                            color="success.contrastText"
                                        >
                                            <IconAi size={12} />
                                        </Box>
                                    )}
                                    {option?.isOwnerLocked && (
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                display: `flex`,
                                                alignItems: `center`,
                                                justifyContent: `center`,
                                                borderRadius: 3.75,
                                                color: theme.palette.common.white,
                                                backgroundColor: theme.palette.etc.gray300,
                                            }}
                                        >
                                            <IconLock size={12} />
                                        </Box>
                                    )}
                                </Stack>
                            ),
                        }}
                        {...(type === "share_asset" && {
                            subName: {
                                name: (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        sx={{
                                            "& span:not(:last-child)": {
                                                pr: theme.spacing(1),
                                                borderRight: `solid 1px ${theme.palette.secondary[100]}`,
                                            },
                                            "& span:not(:first-of-type)": {
                                                pl: theme.spacing(1),
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant={"body3_400"}
                                            color={theme.palette.greySecondary[600]}
                                            component={"span"}
                                        >
                                            {option.extensions}
                                        </Typography>
                                        <Typography
                                            variant={"body3_400"}
                                            color={theme.palette.greySecondary[600]}
                                            component={"span"}
                                        >
                                            {convertToByte(option.size)}
                                        </Typography>
                                    </Grid>
                                ),
                            },
                        })}
                    />
                );
            case "size":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId] ? convertToByte(option?.[columnId]) : "-"}
                        </Typography>
                    </Grid>
                );

            case "count":
                return (
                    <Grid item>
                        <BadgeCollection totalCount={option?.[columnId]} />
                    </Grid>
                );
            case "date": {
                const date = option?.[columnId]?.at
                    ? convertUtcToLocal(option?.[columnId]?.at, "YYYY-MM-DD HH:mm")
                    : "-";
                return <Grid item>{date}</Grid>;
            }
            case "user": {
                const byName = option?.[columnId]?.by
                    ? option?.[columnId]?.by?.name?.length > 8
                        ? `${option?.[columnId]?.by?.name?.slice(0, 8)}...`
                        : option?.[columnId]?.by?.name
                    : "-";
                return (
                    <Grid item>
                        {byName ? (
                            <Button
                                variant={"text"}
                                color={`inherit`}
                                size={"small"}
                                sx={{
                                    minWidth: 0,
                                    textAlign: `left`,
                                }}
                                onClick={(e) => handleUserProfileClick(e, option?.[columnId]?.by?.id)}
                            >
                                {byName}
                            </Button>
                        ) : (
                            "-"
                        )}
                    </Grid>
                );
            }
            case "expireDate": {
                const expiresDate = countDaysBetweenDates({
                    toDate: moment(convertUtcToLocal(option?.[columnId], "YYYY-MM-DD HH:mm:ss")),
                    precise: true,
                });
                if (!expiresDate) return "-";

                return (
                    <Grid container alignItems={"flex-start"} flexDirection={"column"}>
                        <Typography variant={"body1_400"}>{moment(option?.[columnId]).format("YYYY-MM-DD")}</Typography>
                        <Typography variant={"body1_400"}>
                            (
                            {expiresDate >= 0 ? (
                                <>
                                    <Typography variant={"body1_400"} color={theme.palette.warning[600]}>
                                        {expiresDate}
                                    </Typography>
                                    {t(`common::label::${"days left"}`, `days left`)}
                                </>
                            ) : (
                                <>
                                    <Typography variant={"body1_400"} color={theme.palette.error[600]}>
                                        {t(`common::label::${"Expired"}`, `Expired`)}
                                    </Typography>
                                </>
                            )}
                            )
                        </Typography>
                    </Grid>
                );
            }
            case "project":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId]?.name ?? "-"}
                        </Typography>
                    </Grid>
                );
            case "text":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId] ?? "-"}
                        </Typography>
                    </Grid>
                );
            case "downloadsRemaining":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.option?.maxDownloads
                                ? option.option.maxDownloads - (option.option.downloadCount ?? 0)
                                : "-"}
                        </Typography>
                    </Grid>
                );
            default:
                return option?.[columnId] ?? null;
        }
    }, []);

    // 페이지네이션·필터로 행 수가 바뀔 때마다 스크롤바가 생겼다 사라지므로 자리를 상시 확보한다.
    // 없으면 표 폭이 8px 씩 흔들리고 컬럼 너비가 같이 재계산된다.
    if (data?.length > 0)
        return (
            <TableContainer sx={{ overflow: "auto", scrollbarGutter: "stable" }}>
                <Table
                    stickyHeader
                    shadowHeader
                    borderHeader
                    sx={{
                        tableLayout: "fixed",
                        borderTop: `1px solid ${theme.palette.greySecondary[100]}`,
                        minWidth: `${tableMinWidth}px`,
                    }}
                >
                    {showColumns && (
                        <TableHead>
                            <TableRow>
                                {onClickCheck && <TableCell sx={{ width: "32px", px: 0 }} />}
                                {columns.map((column, index) => (
                                    <TableCell
                                        key={`${keyPrefix}-header-cell-${index}`}
                                        sx={{
                                            ...column.style?.custom,
                                        }}
                                    >
                                        {t(`common::label::${column.label}`, column.label)}
                                    </TableCell>
                                ))}
                                <TableCell sx={actionButtonProps ? actionButtonProps : { width: 50 }} />
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {data?.map((item, index) => (
                            <React.Fragment key={`${keyPrefix}-${item.id}`}>
                                <TableRow
                                    hover
                                    role="checkbox"
                                    onClick={() => onClickRow(item)}
                                    selected={!!selected?.find(({ id }) => id === item.id)}
                                    sx={{
                                        ...(onClickRow && {
                                            "&:hover": {
                                                backgroundColor: alpha(theme.palette.greySecondary[50], 0.24),
                                            },
                                            ...(clicked?.id === item.id && {
                                                backgroundColor: `${alpha(
                                                    theme.palette.greySecondary.A100,
                                                    0.36,
                                                )} !important`,
                                            }),
                                        }),
                                    }}
                                >
                                    {onClickCheck && (
                                        <TableCell sx={{ width: "32px", px: 0 }}>
                                            <Checkbox
                                                color={"greySecondary"}
                                                checked={!!selected?.find(({ id }) => id === item.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => onClickCheck(item, e.target.checked)}
                                            />
                                        </TableCell>
                                    )}
                                    {columns.map((column, index) => (
                                        <TableCell
                                            key={`${keyPrefix}-body-cell-${item.id}-${column.id}-${column.type}`}
                                            sx={{
                                                ...column.style?.custom,
                                                paddingLeft: onClickCheck && column.id === "name" ? "0px" : undefined,
                                                flex: column.id === "name" ? 1 : 0,
                                            }}
                                        >
                                            {getTableValueByColumn(column.id, column.type, item)}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={actionButtonProps ? actionButtonProps : { width: "50px" }}>
                                        {actionButton && actionButton(item)}
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
                {!userProfilePending && (
                    <UserDetailPopper
                        id={openUserProfile ? "user-profile-popover" : undefined}
                        handleUserProfileClose={handleUserProfileClose}
                        userProfileAnchorEl={userProfileAnchorEl}
                        openUserProfile={openUserProfile}
                        userProfileData={userProfileData}
                    />
                )}
            </TableContainer>
        );

    return null;
};

export default TableList;
